// ── Темы оформления, шрифты, цвета карточек ──────────────────────────────────
// Импортируй { THEMES, SANS, CARD_COLORS } везде, где нужны цвета.
// Текущая тема C = THEMES[theme] — App кладёт её в ThemeContext (src/ui/theme.js),
// детали интерфейса берут её через usePalette().
//
// Три темы:
//  light, dark — один стиль, «вариант A» (выбран 13.10.2026): нейтральные серые
//    поверхности, тонкие рамки, плоские карточки без теней, один синий акцент, Inter.
//  arcade — «Аркада», в духе игры Balatro (16–17.10.2026): пиксельный шрифт Tiny5 на заголовках и кнопках,
//    живой пиксельный вихрь красного и синего на столе, тёмные панели со светлой
//    окантовкой, сочные кнопки с ребром, бликом и переливом, голографическая рамка у
//    главной плитки, покачивание карты в повторении, вспышка стола на ответ, строчки
//    развёртки (всё, что не цвет, — в index.css под html[data-theme="arcade"]).
//
// Контраст по WCAG 2.2 AA проверяет src/themes.test.js: текст (text, mid, faint) не ниже
// 4.5:1 к pageBg, card и surface; цветной текст (blueDark, green, warmDark, danger,
// pillText) — 4.5:1 к своим подложкам; текст на заливке blue — 4.5:1.
//
// Роли синего: blue — заливка кнопок (текст поверх подбирает readableOn), blueDark —
// синий ТЕКСТ и значки активного пункта, bluePale — подложка выбранного, blueMid —
// волна и подсветка. Везде в приложении: зелёный — к повторению, синий — новые.
//
// Кроме цветов у темы есть форма — детали из src/ui читают её из того же объекта:
//  dark    — тёмная ли тема (значки, системные элементы, полоса окна программы);
//  borderW — толщина рамок деталей, px (1 — тонкая линия, 2 — окантовка «Аркады»);
//  lift    — высота «ребра» объёмных кнопок, px (0 — плоско);
//  panelShadow — тень-«ребро» под панелями (нет у плоских тем: React не пишет undefined);
//  backdrop — фон корня экрана (CSS background): у плоских тем = pageBg, у «Аркады» —
//            прозрачный, чтобы был виден живой стол. Как цвет — всегда pageBg;
//  onAccent, dangerFill — надпись на цветных кнопках и заливка опасной кнопки (если не
//            заданы — подбираются по контрасту и берётся danger);
//  table   — цвета живого фона-вихря (src/arcadeTable.js); нет — фон обычный;
//  face    — лицо карты в повторении (игральная карта); нет — карта цвета панели.

export const THEMES = {
  light: {
    pageBg:"#f8f8f6",pageBg2:"#f0efec",card:"#ffffff",surface:"#f3f2ef",
    border:"#dcdad3",borderSoft:"#e8e6e1",
    blue:"#2563eb",blueMid:"#3b82f6",bluePale:"#e8effd",blueDark:"#1d4ed8",
    warm:"#c2700c",warmPale:"#fdf2e2",warmDark:"#8f4f06",
    green:"#2b764b",greenPale:"#e6f4ea",
    text:"#1c1c1a",mid:"#55544e",faint:"#6e6c65",white:"#ffffff",danger:"#c62828",
    shadow:"rgba(20,20,18,0.06)",shadowMd:"rgba(20,20,18,0.14)",
    glassHdr:"rgba(248,248,246,0.92)",glassCard:"rgba(255,255,255,0.98)",
    waveColor:"#c8c6bf",waveActive:"#2563eb",progressTint:"rgba(37,99,235,0.12)",
    pill:"#e8effd",pillText:"#1d4ed8",trackBg:"#f0efec",trackActive:"#e3ebfd",
    cardShadow:"0 1px 2px rgba(20,20,18,0.05)",
    dark:false,borderW:1,lift:0,backdrop:"#f8f8f6",
  },
  dark: {
    pageBg:"#1c1c1b",pageBg2:"#171716",card:"#232322",surface:"#2a2a28",
    border:"#3d3c39",borderSoft:"#302f2d",
    blue:"#2f6fe0",blueMid:"#5b93f5",bluePale:"#1f2b42",blueDark:"#9fbfff",
    warm:"#e3a13b",warmPale:"#2c2416",warmDark:"#f2c37a",
    green:"#5bbd7e",greenPale:"#17271d",
    text:"#f1f0ec",mid:"#b8b6af",faint:"#94928b",white:"#f1f0ec",danger:"#f0796b",
    shadow:"rgba(0,0,0,0.35)",shadowMd:"rgba(0,0,0,0.55)",
    glassHdr:"rgba(28,28,27,0.92)",glassCard:"rgba(35,35,34,0.98)",
    waveColor:"#4a4944",waveActive:"#5b93f5",progressTint:"rgba(91,147,245,0.22)",
    pill:"#1f2b42",pillText:"#9fbfff",trackBg:"#232322",trackActive:"#1f2b42",
    cardShadow:"0 1px 2px rgba(0,0,0,0.3)",
    dark:true,borderW:1,lift:0,backdrop:"#1c1c1b",
  },
  // «Колода» (17.10, по макету пользователя из Figma; только Pro): тёмно-бирюзовый карточный
  // стол без живого фона, спокойные панели с тонкой рамкой, кремовые игральные карты, акценты —
  // золото (подписи, монета, «Крутить»), кнопки с заливкой — покерные фишки (kit.button "chip":
  // красная фишка C.chip, опасная — чёрная C.chipDanger; src/ui/surface.js chipFace), звуки
  // стола — записи фишек и карт (kit.sounds "table", src/tableSounds.js).
  // Пиксельный шрифт — только на подписях и числах (data-display), остальное — Inter.
  // Своя раскладка главной и бокового меню — kit.layout "hand" (src/screens/HandHome.js).
  deck: {
    pageBg:"#0e3a3f",pageBg2:"#0c3439",card:"#15474d",surface:"#103d42",
    border:"#2b6066",borderSoft:"#1f5056",
    blue:"#2a78c4",blueMid:"#4a9be6",bluePale:"#17405a",blueDark:"#8cc7ff",
    warm:"#d8b75a",warmPale:"#3b3a22",warmDark:"#e9c86a",
    green:"#6fd39b",greenPale:"#14423a",
    text:"#eef3ef",mid:"#bed3cf",faint:"#9dbbb7",white:"#ffffff",danger:"#f39b92",
    shadow:"rgba(0,0,0,0.35)",shadowMd:"rgba(0,0,0,0.55)",
    glassHdr:"#0e3a3f",glassCard:"#15474d",
    waveColor:"#4f8086",waveActive:"#4a9be6",progressTint:"rgba(74,155,230,0.22)",
    pill:"#17405a",pillText:"#8cc7ff",trackBg:"#103d42",trackActive:"#17405a",
    cardShadow:"0 2px 0 rgba(0,0,0,0.25)",
    panelShadow:"inset 0 1px 0 rgba(255,255,255,.04), 0 8px 18px -10px rgba(0,0,0,.55)",
    // Выбранный «чип» на макете — светлый с тёмной надписью.
    chipOnBg:"#dde6e3",chipOnFg:"#12363a",
    dangerFill:"#b84f4a",gold:"#d8b75a",
    // Фишки: заливка, ребро (толщина снизу и обод), вставки по краю, надпись.
    chip:{fill:"#b8322c",edge:"#761c18",insert:"#f4ecd8",ink:"#fffaf0"},
    chipDanger:{fill:"#1f2829",edge:"#080c0d",insert:"#d2564d",ink:"#f4ecd8"},
    kit:{background:"plain",card:"playing",sounds:"table",motion:"calm",layout:"hand",logo:"diamond",button:"chip"},
    dark:true,borderW:1,lift:0,backdrop:"#0e3a3f",
    face:{bg:"#efe9da",surface:"#e5dece",text:"#1d2b2c",mid:"#475453",faint:"#5f6c6b",border:"#b7ae98"},
    cardColors:["#2a78c4","#d8b75a","#6fd39b","#9a7bd6","#c95b55","#3fb7b0"],
  },
  // Цвета Balatro и их роли: синий — «фишки» (у нас новые карточки), красный — «множитель»
  // (опасное, «Снова»), золото — «деньги» (серия, предупреждения), зелёный — к повторению.
  // Панели — тёмный сине-зелёный графит со светлой окантовкой; стол — живой вихрь (table).
  arcade: {
    pageBg:"#162325",pageBg2:"#101a1c",card:"#2e3560",surface:"#262c52",
    border:"#a3acef",borderSoft:"#444c80",
    blue:"#0e90f0",blueMid:"#009dff",bluePale:"#1f3d75",blueDark:"#7cc8ff",
    warm:"#fda200",warmPale:"#4a3a1a",warmDark:"#ffc95e",
    green:"#56cf9c",greenPale:"#1c4a44",
    text:"#ffffff",mid:"#dcd9ee",faint:"#d4d1ea",white:"#ffffff",danger:"#ff9a92",
    shadow:"rgba(0,0,0,0.45)",shadowMd:"rgba(0,0,0,0.7)",
    // Стекло: шапка, боковая и нижняя панели — полупрозрачные, в них виден стол (без размытия:
    // оно тяжёлое для телефона и над живым холстом показывало застрявшие кадры прошлого экрана).
    glassHdr:"rgba(24,28,52,0.58)",glassCard:"rgba(46,53,96,0.94)",
    // Панели (плитки, списки) — полупрозрачное стекло с радужными бликами, «калейдоскоп»:
    // panelTint — сама подложка (по ней themes.test.js проверяет контраст текста над любым
    // цветом стола), сверху — блик в углу и переливы. Без размытия — по той же причине.
    panelTint:"rgba(28,32,64,0.65)",
    panelBg:"radial-gradient(120% 80% at 0% 0%, rgba(255,255,255,0.11), rgba(255,255,255,0) 55%), "
      +"linear-gradient(105deg, rgba(255,255,255,0) 38%, rgba(255,255,255,0.05) 46%, rgba(255,255,255,0) 54%), "
      +"conic-gradient(from 210deg at 85% 0%, rgba(255,90,200,0.10), rgba(80,200,255,0.09), rgba(255,210,90,0.08), rgba(90,255,190,0.08), rgba(170,110,255,0.09), rgba(255,90,200,0.10)), "
      +"rgba(28,32,64,0.65)",
    // Полосы (шапка читалки и т. п.) — то же стекло, что шапка приложения.
    barBg:"rgba(24,28,52,0.58)",
    // Поле ввода — тёмный «колодец» в стекле.
    fieldBg:"rgba(10,12,28,0.72)",
    // Всплывающее (окна, меню, листы снизу) — то же стекло, но плотнее: под ним что угодно.
    floatBg:"radial-gradient(120% 60% at 0% 0%, rgba(255,255,255,0.10), rgba(255,255,255,0) 50%), "
      +"conic-gradient(from 210deg at 85% 0%, rgba(255,90,200,0.08), rgba(80,200,255,0.07), rgba(255,210,90,0.06), rgba(90,255,190,0.06), rgba(170,110,255,0.07), rgba(255,90,200,0.08)), "
      +"rgba(22,26,50,0.97)",
    waveColor:"#7f88c9",waveActive:"#009dff",progressTint:"rgba(0,157,255,0.28)",
    pill:"#1f3d75",pillText:"#7cc8ff",trackBg:"#262c52",trackActive:"#1f3d75",
    cardShadow:"0 4px 0 rgba(0,0,0,0.45)",
    // Набор темы — из каких частей она собрана (кроме цветов): фон, карта в повторении,
    // звуки и движение. App и экраны включают модули по нему (см. themeKit ниже).
    kit:{background:"table",card:"playing",sounds:"arcade",motion:"juicy",logo:"ace"},
    dark:true,borderW:2,lift:4,panelShadow:"inset 0 1px 0 rgba(255,255,255,.18), inset 0 22px 26px -22px rgba(255,255,255,.16), 0 4px 0 rgba(0,0,0,.45), 0 16px 32px -12px rgba(0,0,0,.65), 0 0 22px -5px rgba(0,157,255,.75)",
    // Экран прозрачный: под ним живой вихрь стола (src/arcadeTable.js).
    backdrop:"transparent",
    // Кнопки с заливкой — сочные цвета игры с белой надписью в тёмной обводке (labelOutline:
    // обводку рисует index.css), поэтому для надписи на них хватает контраста 3:1.
    onAccent:"#ffffff",dangerFill:"#fe5f55",labelOutline:true,
    // Стол — мягкая краска, как фон Balatro, без видимых пикселей. Текст прямо на столе
    // читается за счёт тёмного ореола у букв (index.css); белый — не ниже 2.8:1 даже на самом
    // ярком цвете (themes.test.js). Главная — цвета меню игры.
    // Стол — «жидкая краска», как в Balatro: сочно, ярко, жирно (17.10 пользователь: «как в
    // балатаро… убери спираль»). Основа почти чёрная, цвет раздела насыщенный, второй — на
    // гребнях краски. Белый текст на этих цветах — не ниже 2.8 (themes.test.js).
    table:{ink:"#1a0a0e",red:"#d8261f",blue:"#e8692a",light:1,speed:1},
    // По разделам (как блайнды в игре): App переключает, стол плавно перетекает.
    scenes:{
      home:{ink:"#1a0a0e",red:"#d8261f",blue:"#e8692a",light:1,speed:1},
      review:{ink:"#06231a",red:"#17964f",blue:"#b8860b",light:1,speed:1},
      audio:{ink:"#120a2c",red:"#7535f0",blue:"#1a8fd6",light:1,speed:1},
      reading:{ink:"#062028",red:"#10928f",blue:"#b8860b",light:1,speed:1},
      stats:{ink:"#0a1434",red:"#2a62f0",blue:"#1f9e6e",light:1,speed:1},
    },
    // Лицо карты в повторении — кремовая игральная карта с тёмным текстом и мастями.
    // Цвета карточек (метки на дорожке, полоски в списке, рамка карты) — палитра Balatro.
    cardColors:["#009dff","#fda200","#4bc292","#b06cff","#fe5f55","#2ee6d6"],
    face:{bg:"#f4ecd8",surface:"#e9dfc6",text:"#1d1b26",mid:"#454152",faint:"#5e5868",border:"#b9ab88"},
  },
};

// Набор темы (дизайн-токены говорят «какого цвета», набор — «из каких частей»):
//   background: 'plain' — фон цветом страницы; 'table' — живой стол (src/arcadeTable.js, цвета C.table/scenes)
//   card:       'panel' — карта повторения как панель; 'playing' — игральная карта (цвета C.face)
//   sounds:     'none' | 'arcade' | 'table' — звуки и отклик нажатий: синтез и искры «Аркады»
//               (src/arcadeJuice.js) или записи фишек и карт (src/tableSounds.js); настройка ac_ui_sounds
//   motion:     'calm' | 'juicy' — анимации отклика (переворот, частицы, тряска) — index.css и arcadeJuice
//   layout:     'standard' | 'hand' — раскладка главной и бокового меню («Колода»: Daily hand,
//               виджеты в меню, слоты справа — src/screens/HandHome.js, src/components/DeckWidgets.js)
//   logo:       'square' | 'ace' | 'diamond' — логотип (Chrome.js Brand)
//   button:     'flat' | 'chip' — кнопки с заливкой: обычные или покерные фишки (ui/surface.js chipFace)
// Новая тема собирается из готовых частей: палитра + набор.
const KIT_DEFAULT = { background: 'plain', card: 'panel', sounds: 'none', motion: 'calm', layout: 'standard', logo: 'square', button: 'flat' };
export const themeKit = (C) => ({ ...KIT_DEFAULT, ...(C?.kit || {}) });

// Порядок в настройках и в кнопке «следующая тема».
export const THEME_ORDER = ['light', 'dark', 'deck', 'arcade'];

// Название, подпись в меню и значок (src/Icon.js). Надписи — ключи для t(): их переводы
// лежат в src/locales рядом с остальными.
export const THEME_META = {
  light: { name: "Светлая", menu: "Светлая тема", icon: 'sun' },
  dark: { name: "Тёмная", menu: "Тёмная тема", icon: 'moon' },
  deck: { name: "Колода", menu: "Тема «Колода»", icon: 'layers', pro: true },
  arcade: { name: "Аркада", menu: "Тема «Аркада»", icon: 'gamepad', pro: true },
};
// Темы Pro («Колода», «Аркада») — только с тарифом Pro или админам. Выбор хранится как есть
// (тариф может вернуться), а показывается — shownTheme: без Pro вместо такой темы обычная
// той же яркости. pro === null — тариф ещё не известен: показываем выбранную.
export const isProTheme = (k) => !!THEME_META[normalizeTheme(k)]?.pro;
export const shownTheme = (k, pro) => (isProTheme(k) && pro === false ? (isDarkTheme(k) ? 'dark' : 'light') : normalizeTheme(k));

// Сохранённое значение → ключ темы. Незнакомое (прежняя «eye», мусор) — светлая.
export const normalizeTheme = (k) => (typeof k === 'string' && Object.prototype.hasOwnProperty.call(THEMES, k) ? k : 'light');
export const isDarkTheme = (k) => THEMES[normalizeTheme(k)].dark;
// Следующая тема по кругу; без Pro — только обычные.
export const nextTheme = (k, pro = true) => {
  const order = THEME_ORDER.filter((x) => pro !== false || !THEME_META[x].pro);
  return order[(order.indexOf(normalizeTheme(k)) + 1) % order.length] || order[0];
};

// Раньше экраны с аудио имели свою палитру; теперь стиль один на всё приложение.
export const PLAYER_THEMES = THEMES;

// Имя темы по объекту палитры: "light" | "dark" | "arcade".
export function themeNameOf(pal) {
  const k = Object.keys(THEMES).find(key => THEMES[key] === pal);
  return k || "light";
}

// Шрифт интерфейса — переменная страницы --ac-font (index.css). Тема меняет её одну
// (applyThemeToDocument), и все места с fontFamily: SANS переключаются разом.
// Inter лежит в приложении (src/fonts.css), Tiny5 для заголовков «Аркады» — там же
// (переменная --ac-display в index.css).
const INTER = "'Inter',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif";
export const SANS = "var(--ac-font)";
export const FONT = SANS;
export const PLAYER_FONT = SANS;
export const PLAYER_SANS = SANS;

// Для canvas: ctx.font не понимает var(), поэтому — текущий шрифт интерфейса словами.
export function uiFontFamily() {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue('--ac-font').trim();
    if (v) return v;
  } catch { /* нет документа (тесты) */ }
  return 'system-ui,sans-serif';
}

// Шрифт текста для чтения по языку. Не зависит от темы: длинный иностранный текст
// в пиксельном шрифте читать тяжело, поэтому и в «Аркаде» он остаётся обычным.
// Noto для японского/китайского/корейского подгружает сама читалка (ensureCjkFont).
export function getFontForLang(lang) {
  if (lang === 'zh') return "'Noto Sans SC','PingFang SC','Microsoft YaHei',sans-serif";
  if (lang === 'ja') return "'Noto Sans JP','Hiragino Sans','Yu Gothic UI','Meiryo',sans-serif";
  if (lang === 'ko') return "'Noto Sans KR','Apple SD Gothic Neo','Malgun Gothic',sans-serif";
  // Latin, Cyrillic (ru, de, fr, es, it, pt, pl, tr, en)
  return INTER;
}

// Тема на уровне страницы: атрибут html[data-theme] (на него смотрит index.css — шрифт,
// ребро кнопок, экран «Аркады»), цвет системных элементов (прокрутка, выпадающие списки),
// фон за страницей и цвет строки состояния браузера (theme-color). Зовут index.js до
// первого рендера, чтобы не мигнуло светлым, и App при каждой смене темы.
export function applyThemeToDocument(theme) {
  if (typeof document === 'undefined') return;
  const k = normalizeTheme(theme);
  const T = THEMES[k];
  const root = document.documentElement;
  root.dataset.theme = k;
  root.style.colorScheme = T.dark ? 'dark' : 'light';
  root.style.backgroundColor = T.pageBg;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = T.pageBg;
  // Цвета темы — в CSS-переменные: по ним автопроверка вёрстки находит места, нарисованные
  // мимо общих частей (панель сплошным C.card вместо panelSurface, кнопка заливкой C.blue
  // вместо Button), — их не нужно искать глазами.
  root.style.setProperty('--ac-card', T.card);
  root.style.setProperty('--ac-fill', T.blue);
  root.toggleAttribute('data-panel-skin', !!T.panelBg);
}

// Цвета карточек по кругу (назначаются при создании и хранятся в карточке)
export const CARD_COLORS = ["#3b82f6","#f97316","#22c55e","#8b5cf6","#ec4899","#06b6d4"];

// Цвет карточки в этой теме: тема может перекрасить цвета карточек в свою палитру
// (C.cardColors; «Аркада» — цвета Balatro). Берётся цвет палитры с ближайшим оттенком —
// так перекрашиваются и цвета из прежних версий приложения, не только CARD_COLORS.
// В карточке по-прежнему хранится исходный цвет; без C.cardColors или не цвет — как есть.
const hueOf = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), d = max - Math.min(r, g, b);
  if (d < 0.08) return null; // серый — оттенка нет
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return (h * 60 + 360) % 360;
};
export function cardColorIn(C, hex) {
  if (!hex || !C?.cardColors) return hex;
  const h = hueOf(hex);
  if (h == null) return hex;
  let best = hex, dist = 999;
  for (const c of C.cardColors) {
    const ch = hueOf(c);
    const dd = Math.min(Math.abs(ch - h), 360 - Math.abs(ch - h));
    if (dd < dist) { dist = dd; best = c; }
  }
  return best;
}
